import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { reportAPI, userAPI } from '../services/api';

const TechWorkReport = () => {
  const { tenant, user } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState('all');
  const [subDists, setSubDists] = useState([]);
  const [selectedSubDist, setSelectedSubDist] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateMode, setDateMode] = useState('daily'); // daily, weekly, monthly
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const d = new Date(); d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay()+6)%7);
    const w1 = new Date(d.getFullYear(),0,4);
    return 1 + Math.round(((d-w1)/86400000 - 3 + (w1.getDay()+6)%7)/7);
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()+1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedISPTicket, setSelectedISPTicket] = useState('all');
  const [showISPModal, setShowISPModal] = useState(false);
  const [showParkedModal, setShowParkedModal] = useState(false);
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const [selectedSubDistFilter, setSelectedSubDistFilter] = useState('all');
  const customerCategories = ['No Internet Connection','Slow Speed','Router/ONT Issue','Cable Cut','Frequent Disconnection','Billing Issue','New Connection','Others'];
  const internalCategories = ['Switch Configuration','OLT Configuration','MSTP Configuration','Network Upgrade','VLAN Change','Port Configuration','Fiber Splicing','Equipment Installation','Internal Request','Others'];
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryML, setAiSummaryML] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewTech, setPreviewTech] = useState(null);
  const [error, setError] = useState('');
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';

  useEffect(() => {
    userAPI.getTechnicians().then(res => {
      const techOnly = (res.data.technicians || []).filter(t => ["subdist_tech_own", "subdist_tech_isp", "isp_technician", "dist_l1", "dist_l2", "dist_l3"].includes(t.role));
      setTechnicians(techOnly);
    }).catch(() => {});
    // Load subdists
    import('axios').then(({default:axios})=>{
      const token = localStorage.getItem('token');
      axios.get('/api/weekly/subdists',{headers:{Authorization:'Bearer '+token}})
        .then(r=>setSubDists(r.data.subdists||[])).catch(()=>{});
    });
    // Don't auto-load - wait for user to click Generate
  }, []);

  const loadReport = async (date, techId) => {
    setLoading(true);
    setError('');
    setAiSummary('');
    setAiSummaryML('');
    try {
      const params = {};
      // Date mode
      if(dateMode === 'daily') params.date = date || selectedDate;
      else if(dateMode === 'weekly') { params.week_no = selectedWeek; params.year = selectedYear; }
      else if(dateMode === 'monthly') { params.month = selectedMonth; params.year = selectedYear; }
      if ((techId || selectedTech) !== 'all') params.technician_id = techId || selectedTech;
      if (selectedType !== 'all') params.ticket_type = selectedType;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedSubDistFilter !== 'all') params.sub_dist = selectedSubDistFilter;
      const res = await reportAPI.techWorkReport(params);
      setReports(res.data.reports || []);
    } catch (err) {
      setError('Failed to load report');
    } finally { setLoading(false); }
  };

  const generateAISummary = async () => {
    if (reports.length === 0) return;
    setAiLoading(true);
    setAiSummary('');
    setAiSummaryML('');
    try {
      const reportData = reports.map(r => ({
        name: r.technician.name,
        sub_dist: r.technician.sub_dist_code||'',
        total: r.stats.total,
        resolved: r.stats.resolved,
        escalated: r.stats.escalated,
        resolution_rate: r.stats.resolution_rate,
        avg_hours: r.stats.avg_resolution_hours,
        tickets: (() => {
          const cats = {};
          r.tickets.forEach(t => { cats[t.category] = (cats[t.category]||0)+1; });
          return Object.entries(cats).map(([k,v]) => `${k}: ${v}`);
        })()
      }));
      console.log('Sending reportData:', JSON.stringify(reportData?.slice(0,2)));
      // Calculate actual date range for period label
      let period = selectedDate;
      if(dateMode==='weekly') {
        const jan4 = new Date(selectedYear, 0, 4);
        const startMs = jan4.getTime() - ((jan4.getDay()+6)%7)*86400000 + (selectedWeek-1)*7*86400000;
        const wStart = new Date(startMs);
        const wEnd = new Date(startMs + 6*86400000);
        const fmt = d => d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
        period = fmt(wStart) + ' to ' + fmt(wEnd);
      } else if(dateMode==='monthly') {
        const mStart = new Date(selectedYear, selectedMonth-1, 1);
        const mEnd = new Date(selectedYear, selectedMonth, 0);
        const fmt = d => d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
        period = fmt(mStart) + ' to ' + fmt(mEnd);
      }
      const res = await fetch('/api/reports/ai-tech-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify({ reportData, period })
      });
      const aiData = await res.json();
      let analysis = aiData.analysis || '';
      // Strip <think> blocks
      if(analysis.includes('<think>')) {
        const end = analysis.lastIndexOf('</think>');
        if(end !== -1) analysis = analysis.substring(end + 8).trim();
      }
      // If thinking is mixed in (no tags), find last 📊 marker
      const lastEmoji = analysis.lastIndexOf('\n📊');
      if(lastEmoji > 100) analysis = analysis.substring(lastEmoji).trim();
      // Remove lines containing thinking keywords
      const cleanLines = analysis.split('\n').filter(l => 
        !l.includes('**Data Provided') && 
        !l.includes('Draft -') && 
        !l.includes('Check Against') &&
        !l.includes('Identify Constraints') &&
        !l.trim().match(/^\d+\.\s+\*\*/)
      );
      analysis = cleanLines.join('\n').trim();
      setAiSummary(analysis || 'No analysis generated');
      setAiSummaryML(''); // Malayalam not used with Groq
    } catch (err) {
      setError('AI generation failed: ' + err.message);
    } finally { setAiLoading(false); }
  };

  const downloadAIPdf = () => {
    if(!aiSummary) return;
    const printWindow = window.open('', '_blank');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>PDIC Performance Report</title>
  <style>
    body { font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.6; color: #1e293b; padding: 30px; max-width: 800px; margin: 0 auto; }
    pre { white-space: pre-wrap; word-wrap: break-word; }
    .header { text-align: center; margin-bottom: 20px; }
    .header h1 { font-size: 16px; color: #1e3a5f; }
    .header p { font-size: 11px; color: #64748b; }
    @media print { body { padding: 15px; } button { display: none !important; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>PDIC KeralaVision</h1>
    <p>Technician Performance Report — Generated on ${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>
  </div>
  <pre>${aiSummary.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>
  <br>
  <button onclick="window.print()" style="padding:10px 20px;background:#1e3a5f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:14px;">🖨️ Print / Save as PDF</button>
</body>
</html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownload = () => {
    if (reports.length === 0) return;
    const rows = [
      [tenant ? tenant.branding.app_name : 'FibraDesk', 'Tech Work Report', selectedDate],
      [''],
      ['Technician', 'Total', 'Resolved', 'Escalated', 'Pending', 'Rate %', 'Avg Hours'],
      ...reports.map(r => [r.technician.name, r.stats.total, r.stats.resolved, r.stats.escalated, r.stats.pending, r.stats.resolution_rate + '%', r.stats.avg_resolution_hours + 'h']),
      [''],
      ['TICKET DETAILS'],
      ['Technician', 'Ticket No', 'Customer', 'Category', 'Status', 'Area', 'LCO'],
      ...reports.flatMap(r => r.tickets.map(t => [r.technician.name, t.ticket_number, t.customer, t.category, t.status, t.isp_ticket_id||'-', t.area, t.lco])),
      [''],
      ['AI SUMMARY (English)'],
      [aiSummary || 'Not generated'],
      [''],
      ['AI SUMMARY (Malayalam)'],
      [aiSummaryML || 'Not generated']
    ];
    const csv = rows.map(r => r.map(c => '"' + String(c || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'TechWorkReport_' + selectedDate + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 80) return '#4caf50';
    if (rate >= 60) return '#ff9800';
    return '#f44336';
  };

  const getPerformanceLabel = (rate) => {
    if (rate >= 80) return 'Excellent';
    if (rate >= 60) return 'Good';
    if (rate >= 40) return 'Average';
    return 'Needs Improvement';
  };

  const statusColor = { open: '#2196f3', assigned: '#9c27b0', in_progress: '#ff9800', escalated: '#f44336', resolved: '#4caf50', closed: '#757575' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', color: '#333' }}>🧑‍🔧 Tech Work Reports</h2>
          <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>AI-powered daily technician performance reports</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
<button onClick={handleDownload} disabled={reports.length === 0} style={{ padding: '10px 16px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '6px', cursor: reports.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>⬇️ Download CSV</button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>Period</label>
            <div style={{ display:'flex', borderRadius:'6px', overflow:'hidden', border:'1px solid #ddd' }}>
              {[['daily','Daily'],['weekly','Weekly'],['monthly','Monthly']].map(([m,l])=>(
                <button key={m} onClick={()=>setDateMode(m)} style={{ flex:1, padding:'9px 4px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', background:dateMode===m?primaryColor:'#fff', color:dateMode===m?'#fff':'#64748b' }}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              {dateMode==='daily'?'Date':dateMode==='weekly'?'Week':'Month'}
            </label>
            {dateMode==='daily' && <input type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }} />}
            {dateMode==='weekly' && (
              <select value={selectedWeek} onChange={e=>setSelectedWeek(parseInt(e.target.value))} style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }}>
                {Array.from({length:52},(_,i)=><option key={i+1} value={i+1}>Week {i+1}</option>)}
              </select>
            )}
            {dateMode==='monthly' && (
              <select value={selectedMonth} onChange={e=>setSelectedMonth(parseInt(e.target.value))} style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }}>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i)=>(
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>Sub-Distribution</label>
            <select value={selectedSubDistFilter} onChange={e => setSelectedSubDistFilter(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="all">All Sub-Dists</option>
              {subDists.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>Technician</label>
            <select value={selectedTech} onChange={e => setSelectedTech(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="all">All Technicians</option>
              {technicians
                .filter(t => selectedSubDistFilter==='all' || t.sub_dist_code===selectedSubDistFilter)
                .map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>Ticket Type</label>
            <select value={selectedType} onChange={e => { setSelectedType(e.target.value); setSelectedCategory('all'); }} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="all">All Types</option>
              <option value="customer">👤 Customer Complaints</option>
              <option value="internal">🔧 Internal Work Orders</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>Category</label>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="all">All Categories</option>
              {(selectedType==='internal' ? internalCategories : selectedType==='customer' ? customerCategories : [...customerCategories,...internalCategories]).map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>ISP Ticket</label>
            <select value={selectedISPTicket} onChange={e => setSelectedISPTicket(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value='all'>All Tickets</option>
              <option value='yes'>✅ Has ISP Ticket</option>
              <option value='no'>❌ No ISP Ticket</option>
            </select>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => { setShowPreview(false); loadReport(selectedDate, selectedTech); }} disabled={loading} style={{ padding: '10px 20px', background: loading ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px' }}>
              {loading ? 'Loading...' : '🔍 Generate'}
            </button>
          </div>
        </div>
      </div>

      {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>}

      {/* Repeat Customers Modal */}
      {showRepeatModal && (() => {
        // Build repeat customer data from all tickets
        const allTix = reports.flatMap(r => r.tickets.filter(t=>t.ticket_type!=='internal').map(t=>({...t, techName:r.technician.name})));
        const custMap = {};
        allTix.forEach(t=>{
          const key = t.connection_id||t.phone||'';
          if(!key) return;
          if(!custMap[key]) custMap[key]={name:t.customer,phone:t.phone,connection_id:t.connection_id,tickets:[]};
          custMap[key].tickets.push(t);
        });
        const repeats = Object.values(custMap).filter(c=>c.tickets.length>1).sort((a,b)=>b.tickets.length-a.tickets.length);
        return (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'24px', width:'100%', maxWidth:'900px', maxHeight:'80vh', overflow:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h3 style={{ margin:0, color:'#be185d' }}>🔄 Repeat Customers</h3>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={()=>{ const w=window.open('','_blank'); w.document.write('<html><head><title>Repeat Customers</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left}th{background:#f8fafc;font-weight:600}</style></head><body><h2 style="color:#be185d">Repeat Customers Report</h2>'+document.getElementById('repeatTable').outerHTML+'</body></html>'); w.document.close(); w.print(); }} style={{ background:'#be185d', color:'white', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>🖨️ Print</button>
                <button onClick={()=>setShowRepeatModal(false)} style={{ background:'#f1f5f9', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>✕ Close</button>
              </div>
            </div>
            <div style={{ marginBottom:'12px', fontSize:'13px', color:'#64748b' }}>{repeats.length} customers raised tickets more than once during this period</div>
            <table id='repeatTable' style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Customer','Connection ID','Phone','Count','Issues','Technician(s)','Last Status'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:'600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repeats.map((cust,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9', background: cust.tickets.length>=3?'#fff1f2':i%2===0?'':'#f8fafc' }}>
                    <td style={{ padding:'8px 10px', fontWeight:'600' }}>{cust.name}</td>
                    <td style={{ padding:'8px 10px', color:'#2563eb' }}>{cust.connection_id||'-'}</td>
                    <td style={{ padding:'8px 10px', color:'#64748b' }}>{cust.phone||'-'}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center' }}><span style={{ background: cust.tickets.length>=3?'#fef2f2':'#fef9c3', color: cust.tickets.length>=3?'#dc2626':'#854d0e', padding:'2px 8px', borderRadius:'10px', fontWeight:'700' }}>{cust.tickets.length}x</span></td>
                    <td style={{ padding:'8px 10px', fontSize:'11px' }}>
                      {cust.tickets.map((t,ti)=>(
                        <div key={ti} style={{ marginBottom: ti<cust.tickets.length-1?'6px':'0', paddingBottom: ti<cust.tickets.length-1?'6px':'0', borderBottom: ti<cust.tickets.length-1?'1px dashed #e2e8f0':'none' }}>
                          <div style={{ fontWeight:'600', color:'#334155', fontSize:'11px' }}>{t.ticket_number}</div>
                          <div style={{ color:'#64748b', fontSize:'10px' }}>{t.category}</div>
                          <div style={{ color:'#94a3b8', fontSize:'10px' }}>{new Date(t.created).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                          <div style={{ color:'#64748b', fontSize:'10px' }}>Tech: {t.techName}</div>
                        </div>
                      ))}
                    </td>
                    <td style={{ padding:'8px 10px', fontSize:'11px' }}>
                      {cust.tickets.map((t,ti)=>(
                        <div key={ti} style={{ marginBottom: ti<cust.tickets.length-1?'6px':'0' }}>
                          <span style={{ background: t.status==='resolved'||t.status==='closed'?'#dcfce7':'#fef2f2', color: t.status==='resolved'||t.status==='closed'?'#15803d':'#dc2626', padding:'2px 6px', borderRadius:'8px', fontSize:'10px', fontWeight:'600' }}>{t.status}</span>
                          {(t.status==='resolved'||t.status==='closed') && t.updated && <div style={{ fontSize:'9px', color:'#94a3b8', marginTop:'2px' }}>Resolved: {new Date(t.updated).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</div>}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
                {repeats.length===0 && <tr><td colSpan={7} style={{ textAlign:'center', padding:'20px', color:'#64748b' }}>No repeat customers in this period</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* Parked Tickets Modal */}
      {showParkedModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'24px', width:'100%', maxWidth:'800px', maxHeight:'80vh', overflow:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h3 style={{ margin:0, color:'#0891b2' }}>🅿️ Parked Tickets</h3>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={()=>{ const w=window.open('','_blank'); w.document.write('<html><head><title>Parked Tickets</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left}th{background:#f8fafc;font-weight:600;color:#64748b}</style></head><body><h2 style="color:#0891b2">Parked Tickets</h2>'+document.getElementById('parkedTicketTable').outerHTML+'</body></html>'); w.document.close(); w.print(); }} style={{ background:'#0891b2', color:'white', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>🖨️ Print</button>
                <button onClick={()=>setShowParkedModal(false)} style={{ background:'#f1f5f9', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>✕ Close</button>
              </div>
            </div>
            <table id='parkedTicketTable' style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Technician','Ticket#','Customer','Category','Park Reason','Parked Since'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:'600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.flatMap(r => r.tickets.filter(t=>t.status==='parked').map(t=>({...t, techName:r.technician.name}))).map((t,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9', background:i%2===0?'':'#f8fafc' }}>
                    <td style={{ padding:'8px 10px', fontWeight:'600', color:'#334155' }}>{t.techName}</td>
                    <td style={{ padding:'8px 10px', color:'#2563eb', fontWeight:'600' }}>{t.ticket_number}</td>
                    <td style={{ padding:'8px 10px' }}>{t.customer}</td>
                    <td style={{ padding:'8px 10px', color:'#64748b' }}>{t.category}</td>
                    <td style={{ padding:'8px 10px' }}><span style={{ background:'#e0f7fa', color:'#0891b2', padding:'2px 8px', borderRadius:'10px', fontWeight:'600', fontSize:'11px' }}>{t.park_reason||'-'}</span></td>
                    <td style={{ padding:'8px 10px', color:'#94a3b8', fontSize:'11px' }}>{t.park_parked_at ? new Date(t.park_parked_at).toLocaleString('en-IN') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ISP Tickets Modal */}
      {showISPModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'24px', width:'100%', maxWidth:'800px', maxHeight:'80vh', overflow:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h3 style={{ margin:0, color:'#1d4ed8' }}>🎫 ISP Tickets Raised</h3>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={()=>{
                  const printContent = document.getElementById('ispTicketTable').outerHTML;
                  const win = window.open('','_blank');
                  win.document.write('<html><head><title>ISP Tickets</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left}th{background:#f8fafc;font-weight:600;color:#64748b}tr:nth-child(even){background:#f8fafc}</style></head><body><h2 style="color:#1d4ed8">ISP Tickets Raised</h2>'+printContent+'</body></html>');
                  win.document.close();
                  win.print();
                }} style={{ background:'#1d4ed8', color:'white', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>🖨️ Print</button>
                <button onClick={()=>setShowISPModal(false)} style={{ background:'#f1f5f9', border:'none', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'13px' }}>✕ Close</button>
              </div>
            </div>
            <table id='ispTicketTable' style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Technician','Ticket#','ISP Ticket ID','Customer','Category','Status','Resolved At'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:'600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.flatMap(r => r.tickets.filter(t=>t.isp_ticket_id&&t.isp_ticket_id!=='').map(t=>({...t, techName:r.technician.name}))).map((t,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'8px 10px', fontWeight:'600', color:'#334155' }}>{t.techName}</td>
                    <td style={{ padding:'8px 10px', color:'#2563eb', fontWeight:'600' }}>{t.ticket_number}</td>
                    <td style={{ padding:'8px 10px' }}><span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'2px 8px', borderRadius:'10px', fontWeight:'700', fontSize:'11px' }}>{t.isp_ticket_id}</span></td>
                    <td style={{ padding:'8px 10px' }}>{t.customer}</td>
                    <td style={{ padding:'8px 10px', color:'#64748b' }}>{t.category}</td>
                    <td style={{ padding:'8px 10px' }}><span style={{ background:t.status==='resolved'||t.status==='closed'?'#dcfce7':'#fef9c3', color:t.status==='resolved'||t.status==='closed'?'#15803d':'#854d0e', padding:'2px 6px', borderRadius:'10px', fontSize:'11px', fontWeight:'600' }}>{t.status}</span></td>
                    <td style={{ padding:'8px 10px', color:'#94a3b8', fontSize:'11px' }}>{t.status==='resolved'||t.status==='closed' ? new Date(t.updated).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reports.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid ' + primaryColor, textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 4px', color: primaryColor, fontSize: '28px' }}>{reports.length}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Technicians</p>
            </div>
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #2196f3', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 4px', color: '#2196f3', fontSize: '28px' }}>{reports.reduce((s, r) => s + r.stats.total, 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Total Tickets</p>
            </div>
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #4caf50', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 4px', color: '#4caf50', fontSize: '28px' }}>{reports.reduce((s, r) => s + r.stats.resolved, 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Resolved</p>
            </div>
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #f44336', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 4px', color: '#f44336', fontSize: '28px' }}>{reports.reduce((s, r) => s + r.stats.escalated, 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Escalated</p>
            </div>
            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #ff9800', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 4px', color: '#ff9800', fontSize: '28px' }}>{reports.length > 0 ? Math.round(reports.reduce((s, r) => s + r.stats.resolution_rate, 0) / reports.length) : 0}%</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Avg Resolution Rate</p>
            </div>
            <div onClick={()=>setShowISPModal(true)} style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #1d4ed8', textAlign: 'center', cursor:'pointer' }}>
              <h3 style={{ margin: '0 0 4px', color: '#1d4ed8', fontSize: '28px' }}>{reports.reduce((s, r) => s + r.tickets.filter(t=>t.isp_ticket_id&&t.isp_ticket_id!=='').length, 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>ISP Tickets Raised</p>
              <p style={{ margin: '4px 0 0', color: '#1d4ed8', fontSize: '11px' }}>Click to view details</p>
            </div>
            <div onClick={()=>setShowParkedModal(true)} style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #0891b2', textAlign: 'center', cursor:'pointer' }}>
              <h3 style={{ margin: '0 0 4px', color: '#0891b2', fontSize: '28px' }}>{reports.reduce((s, r) => s + (r.stats.parked||0), 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Parked Tickets</p>
              <p style={{ margin: '4px 0 0', color: '#0891b2', fontSize: '11px' }}>Click to view details</p>
            </div>
            <div onClick={()=>setShowRepeatModal(true)} style={{ background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid #be185d', textAlign: 'center', cursor:'pointer' }}>
              <h3 style={{ margin: '0 0 4px', color: '#be185d', fontSize: '28px' }}>{reports.reduce((s, r) => s + (r.stats.repeat_customers||0), 0)}</h3>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>Repeat Customers</p>
              <p style={{ margin: '4px 0 0', color: '#be185d', fontSize: '11px' }}>Click to view details</p>
            </div>
          </div>

          {/* Summary Table */}
          <div style={{ background:'white', borderRadius:'10px', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', marginBottom:'16px', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Technician','Sub-Dist','Total','Resolved','In Progress','Escalated','Rate','Avg Hrs','KRA Score',''].map(h=>(
                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map(report=>(
                  <tr key={report.technician.id} style={{ borderTop:'1px solid #f1f5f9', background: previewTech===report.technician.id?'#eff6ff':'' }}>
                    <td style={{ padding:'10px 12px', fontWeight:'600' }}>{report.technician.name}</td>
                    <td style={{ padding:'10px 12px', fontSize:'12px', color:'#64748b' }}>{report.technician.sub_dist_code||'-'}</td>
                    <td style={{ padding:'10px 12px', fontWeight:'700', color:'#2563eb' }}>{report.stats.total}</td>
                    <td style={{ padding:'10px 12px', color:'#15803d', fontWeight:'600' }}>{report.stats.resolved}</td>
                    <td style={{ padding:'10px 12px', color:'#d97706' }}>{report.stats.in_progress}</td>
                    <td style={{ padding:'10px 12px', color:'#dc2626' }}>{report.stats.escalated}</td>
                    <td style={{ padding:'10px 12px' }}>
                      <span style={{ padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:'700',
                        background: report.stats.resolution_rate>=80?'#dcfce7':report.stats.resolution_rate>=50?'#fef9c3':'#fef2f2',
                        color: report.stats.resolution_rate>=80?'#15803d':report.stats.resolution_rate>=50?'#854d0e':'#dc2626' }}>
                        {report.stats.resolution_rate}%
                      </span>
                    </td>
                    <td style={{ padding:'10px 12px', color:'#7c3aed' }}>{report.stats.avg_resolution_hours}h</td>
                    <td style={{ padding:'10px 12px' }}>
                      {report.stats.kra_score !== undefined ? (
                        <div>
                          <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'800',
                            background: report.stats.kra_score>=80?'#dcfce7':report.stats.kra_score>=60?'#fef9c3':'#fef2f2',
                            color: report.stats.kra_score>=80?'#15803d':report.stats.kra_score>=60?'#854d0e':'#dc2626' }}>
                            {report.stats.kra_score}%
                          </span>
                          <div style={{ fontSize:'9px', color:'#94a3b8', marginTop:'3px' }}>
                            SLA:{report.stats.kra_breakdown?.sla}% Spd:{report.stats.kra_breakdown?.speed}% Esc:{report.stats.kra_breakdown?.no_escalation}%
                          </div>
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      {report.tickets.length>0 && (
                        <button onClick={()=>setPreviewTech(previewTech===report.technician.id?null:report.technician.id)}
                          style={{ padding:'4px 12px', background:previewTech===report.technician.id?'#1e293b':'#2563eb', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                          {previewTech===report.technician.id?'▲ Hide':'👁 Preview'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Preview Panel */}
          {previewTech && reports.filter(r=>r.technician.id===previewTech).map(report=>(
            <div key={report.technician.id} style={{ background:'white', borderRadius:'10px', padding:'20px', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', marginBottom:'16px', border:'2px solid #2563eb' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
                <div>
                  <div style={{ fontWeight:'700', fontSize:'15px' }}>{report.technician.name}</div>
                  <div style={{ fontSize:'12px', color:'#64748b' }}>{report.technician.phone} • {report.technician.role}</div>
                </div>
                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                  {selectedType!=='all' && <span style={{ fontSize:'11px', background:'#fef9c3', color:'#854d0e', padding:'3px 10px', borderRadius:'20px', fontWeight:'600' }}>{selectedType==='internal'?'🔧 Internal':'👤 Customer'}</span>}
                  {selectedCategory!=='all' && <span style={{ fontSize:'11px', background:'#f0fdf4', color:'#15803d', padding:'3px 10px', borderRadius:'20px', fontWeight:'600' }}>{selectedCategory}</span>}
                  <button onClick={()=>setPreviewTech(null)} style={{ padding:'4px 12px', background:'#f1f5f9', color:'#64748b', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px' }}>✕ Close</button>
                </div>
              </div>
              {/* KRA Performance Section */}
              {(()=>{
                const kra = report.stats.kra_score;
                const kb = report.stats.kra_breakdown||{};
                const grade = kra>=90?'A+':kra>=80?'A':kra>=70?'B+':kra>=60?'B':kra>=50?'C':'D';
                const gradeColor = kra>=80?'#15803d':kra>=60?'#854d0e':'#dc2626';
                const gradeBg = kra>=80?'#dcfce7':kra>=60?'#fef9c3':'#fef2f2';

                // Category breakdown
                const catMap = {};
                report.tickets.filter(t=>t.ticket_type!=='internal').forEach(t=>{
                  if(!t.category) return;
                  if(!catMap[t.category]) catMap[t.category]={count:0,totalHours:0,resolved:0};
                  catMap[t.category].count++;
                  if(t.status==='resolved'||t.status==='closed'){
                    catMap[t.category].resolved++;
                    const hrs=(new Date(t.updated)-new Date(t.created))/(1000*60*60);
                    catMap[t.category].totalHours+=hrs;
                  }
                });

                const catTargets={'No Internet Connection':4,'Slow Speed':2,'Router/ONT Issue':8,'Cable Cut':24,'Frequent Disconnection':6,'Billing Issue':2,'New Connection':48,'Others':24};

                const metrics = [
                  {label:'SLA Compliance', val:kb.sla||0, weight:'40%', icon:'⏱️', desc:'Resolved before SLA deadline'},
                  {label:'Resolution Speed', val:kb.speed||0, weight:'30%', icon:'⚡', desc:'Actual vs expected category time'},
                  {label:'No Escalation', val:kb.no_escalation||0, weight:'20%', icon:'🎯', desc:'Tickets closed without escalation'},
                  {label:'First Time Fix', val:kb.first_time||0, weight:'10%', icon:'✅', desc:'Resolved without reopening'},
                ];

                return (
                  <div style={{marginBottom:'16px'}}>
                    {/* Header */}
                    <div style={{background:'linear-gradient(135deg,#1e293b,#334155)',borderRadius:'12px',padding:'16px 20px',marginBottom:'12px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        <div style={{color:'#94a3b8',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'4px'}}>KRA Performance Report</div>
                        <div style={{color:'white',fontSize:'14px',fontWeight:'700'}}>{report.technician.name}</div>
                        <div style={{color:'#94a3b8',fontSize:'11px',marginTop:'2px'}}>{report.stats.total} tickets • {report.stats.resolved} resolved • {report.stats.avg_resolution_hours}h avg time</div>
                      </div>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:'36px',fontWeight:'900',color:'white',lineHeight:1}}>{kra}%</div>
                        <div style={{background:gradeBg,color:gradeColor,padding:'3px 12px',borderRadius:'20px',fontWeight:'800',fontSize:'13px',marginTop:'6px'}}>Grade: {grade}</div>
                      </div>
                    </div>

                    {/* KRA Metrics with progress bars */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'12px'}}>
                      {metrics.map((m,i)=>(
                        <div key={i} style={{background:'#f8fafc',borderRadius:'10px',padding:'12px 14px',border:'1px solid #e2e8f0'}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                              <span style={{fontSize:'14px'}}>{m.icon}</span>
                              <span style={{fontSize:'11px',fontWeight:'700',color:'#334155'}}>{m.label}</span>
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                              <span style={{fontSize:'9px',color:'#94a3b8'}}>wt:{m.weight}</span>
                              <span style={{fontSize:'13px',fontWeight:'800',color:m.val>=80?'#15803d':m.val>=60?'#854d0e':'#dc2626'}}>{m.val}%</span>
                            </div>
                          </div>
                          <div style={{background:'#e2e8f0',borderRadius:'4px',height:'6px',overflow:'hidden'}}>
                            <div style={{width:m.val+'%',height:'100%',borderRadius:'4px',background:m.val>=80?'#16a34a':m.val>=60?'#d97706':'#dc2626',transition:'width .5s'}}></div>
                          </div>
                          <div style={{fontSize:'9px',color:'#94a3b8',marginTop:'4px'}}>{m.desc}</div>
                        </div>
                      ))}
                    </div>

                    {/* Category breakdown */}
                    <div style={{background:'#f8fafc',borderRadius:'10px',padding:'12px 14px',border:'1px solid #e2e8f0'}}>
                      <div style={{fontWeight:'700',fontSize:'11px',color:'#334155',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'.5px'}}>📋 Performance by Category</div>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                        <thead>
                          <tr style={{background:'#e2e8f0'}}>
                            {['Category','Tickets','Resolved','Avg Time','Target','Score'].map(h=>(
                              <th key={h} style={{padding:'5px 8px',textAlign:'left',color:'#64748b',fontWeight:'600'}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(catMap).map(([cat,d],i)=>{
                            const target = catTargets[cat]||24;
                            const avg = d.resolved>0?Math.round(d.totalHours/d.resolved*10)/10:0;
                            const score = d.resolved>0?Math.min(100,Math.round((target/Math.max(avg,0.1))*100)):null;
                            return (
                              <tr key={i} style={{borderTop:'1px solid #e2e8f0',background:i%2===0?'white':'#f8fafc'}}>
                                <td style={{padding:'5px 8px',fontWeight:'600',color:'#334155'}}>{cat}</td>
                                <td style={{padding:'5px 8px',textAlign:'center'}}>{d.count}</td>
                                <td style={{padding:'5px 8px',textAlign:'center',color:'#15803d',fontWeight:'600'}}>{d.resolved}</td>
                                <td style={{padding:'5px 8px',textAlign:'center',color:'#7c3aed',fontWeight:'600'}}>{avg>0?avg+'h':'-'}</td>
                                <td style={{padding:'5px 8px',textAlign:'center',color:'#64748b'}}>{target}h</td>
                                <td style={{padding:'5px 8px',textAlign:'center'}}>
                                  {score!==null?<span style={{background:score>=80?'#dcfce7':score>=60?'#fef9c3':'#fef2f2',color:score>=80?'#15803d':score>=60?'#854d0e':'#dc2626',padding:'2px 8px',borderRadius:'10px',fontWeight:'700'}}>{Math.min(score,100)}%</span>:'-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              <table id='ispTicketTable' style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['Ticket#','Customer','Phone','Category','Type','ISP Ticket','Area','Status','Created'].map(h=>(
                      <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:'600' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.tickets.filter(t => selectedISPTicket==='all' || (selectedISPTicket==='yes' ? t.isp_ticket_id&&t.isp_ticket_id!=='' : !t.isp_ticket_id||t.isp_ticket_id==='')).map((t,i)=>(
                    <tr key={i} style={{ borderTop:'1px solid #f1f5f9', background: t.ticket_type==='internal'?'#fffbeb':'' }}>
                      <td style={{ padding:'8px 10px', fontWeight:'600', color:'#2563eb' }}>{t.ticket_number}</td>
                      <td style={{ padding:'8px 10px' }}>{t.ticket_type==='internal'?'-':t.customer}</td>
                      <td style={{ padding:'8px 10px', color:'#64748b' }}>{t.ticket_type==='internal'?'-':t.phone}</td>
                      <td style={{ padding:'8px 10px' }}>{t.category}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ fontSize:'10px', padding:'2px 6px', borderRadius:'10px', fontWeight:'700',
                          background: t.ticket_type==='internal'?'#fef9c3':'#dbeafe',
                          color: t.ticket_type==='internal'?'#854d0e':'#1d4ed8' }}>
                          {t.ticket_type==='internal'?'🔧 Internal':'👤 Customer'}
                        </span>
                      </td>
                      <td style={{ padding:'8px 10px' }}>{t.isp_ticket_id ? <span style={{ fontSize:'10px', padding:'2px 6px', borderRadius:'10px', fontWeight:'700', background:'#eff6ff', color:'#1d4ed8' }}>{t.isp_ticket_id}</span> : <span style={{ color:'#94a3b8' }}>-</span>}</td>
                      <td style={{ padding:'8px 10px', color:'#64748b' }}>{t.area}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ fontSize:'10px', padding:'2px 6px', borderRadius:'10px', fontWeight:'600',
                          background: t.status==='resolved'||t.status==='closed'?'#dcfce7':t.status==='escalated'?'#fef2f2':'#fef9c3',
                          color: t.status==='resolved'||t.status==='closed'?'#15803d':t.status==='escalated'?'#dc2626':'#854d0e' }}>
                          {t.status}
                        </span>
                      </td>
                      <td style={{ padding:'8px 10px', color:'#94a3b8', fontSize:'11px' }}>{t.created ? new Date(t.created).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) + ' ' + new Date(t.created).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* OLD EXPANDED CARDS - HIDDEN */}
          {false && reports.map(report => (
            <div key={report.technician.id} style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '50px', height: '50px', background: primaryColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '20px' }}>
                    {report.technician.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px', color: '#333' }}>{report.technician.name}</h3>
                    <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>{report.technician.phone} • {report.technician.role}</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right', display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: getPerformanceColor(report.stats.resolution_rate) }}>{report.stats.resolution_rate}%</div>
                  <div style={{ background: getPerformanceColor(report.stats.resolution_rate) + '20', color: getPerformanceColor(report.stats.resolution_rate), padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{getPerformanceLabel(report.stats.resolution_rate)}</div>
                  {report.tickets.length > 0 && (
                    <button onClick={()=>setPreviewTech(previewTech===report.technician.id?null:report.technician.id)}
                      style={{ padding:'5px 14px', background: previewTech===report.technician.id?'#1e293b':'#f1f5f9', color: previewTech===report.technician.id?'#fff':'#64748b', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                      {previewTech===report.technician.id ? '▲ Hide' : '👁 Preview'}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '16px' }}>
                {[
                  { label: 'Total', value: report.stats.total, color: '#2196f3' },
                  { label: 'Resolved', value: report.stats.resolved, color: '#4caf50' },
                  { label: 'In Progress', value: report.stats.in_progress, color: '#ff9800' },
                  { label: 'Escalated', value: report.stats.escalated, color: '#f44336' },
                  { label: 'Avg Hours', value: report.stats.avg_resolution_hours + 'h', color: '#9c27b0' }
                ].map(s => (
                  <div key={s.label} style={{ background: s.color + '10', borderRadius: '8px', padding: '12px', textAlign: 'center', border: '1px solid ' + s.color + '30' }}>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {report.tickets.length > 0 && previewTech===report.technician.id && (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                    <h4 style={{ margin: '0', color: '#333', fontSize: '14px' }}>
                      Ticket Details 
                      <span style={{ marginLeft:'8px', fontSize:'11px', background:'#dbeafe', color:'#1d4ed8', padding:'2px 8px', borderRadius:'20px' }}>{report.tickets.length} tickets</span>
                      {selectedType!=='all' && <span style={{ marginLeft:'6px', fontSize:'11px', background:'#fef9c3', color:'#854d0e', padding:'2px 8px', borderRadius:'20px' }}>{selectedType==='internal'?'🔧 Internal':'👤 Customer'}</span>}
                      {selectedCategory!=='all' && <span style={{ marginLeft:'6px', fontSize:'11px', background:'#f0fdf4', color:'#15803d', padding:'2px 8px', borderRadius:'20px' }}>{selectedCategory}</span>}
                    </h4>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Ticket</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Customer</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Category</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Area</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.tickets.map((t, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px', fontWeight: '600', color: primaryColor }}>{t.ticket_number}</td>
                          <td style={{ padding: '8px' }}>{t.customer}<br/><span style={{ color: '#999', fontSize: '11px' }}>{t.phone}</span></td>
                          <td style={{ padding: '8px' }}>{t.category}</td>
                          <td style={{ padding: '8px', color: '#666' }}>{t.area}</td>
                          <td style={{ padding: '8px' }}><span style={{ background: (statusColor[t.status] || '#999') + '20', color: statusColor[t.status] || '#999', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{t.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {report.tickets.length === 0 && <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No tickets assigned today</p>}
            </div>
          ))}

          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px', border: '2px solid ' + primaryColor }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', color: primaryColor }}>🤖 AI Performance Report</h3>
                <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>Auto-generated professional performance analysis</p>
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={generateAISummary} disabled={aiLoading || reports.length===0}
                  style={{ padding: '10px 20px', background: aiLoading||reports.length===0?'#ccc':primaryColor, color:'white', border:'none', borderRadius:'6px', cursor:aiLoading||reports.length===0?'not-allowed':'pointer', fontWeight:'600' }}>
                  {aiLoading ? '⏳ Generating...' : '✨ Generate AI Report'}
                </button>
                {aiSummary && (
                  <button onClick={downloadAIPdf}
                    style={{ padding:'10px 16px', background:'#0f172a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
                    📄 Download PDF
                  </button>
                )}
              </div>
            </div>
            {!aiSummary && !aiLoading && (
              <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>Click "Generate AI Report" to create a professional performance analysis</p>
              </div>
            )}
            {aiLoading && (
              <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '30px', textAlign: 'center' }}>
                <p style={{ margin: '0', color: '#666' }}>⏳ AI is analyzing ticket data and generating professional report...</p>
              </div>
            )}
            {aiSummary && (
              <div style={{ background:'#0f172a', borderRadius:'10px', padding:'20px' }}>
                <div style={{ fontFamily:'monospace', fontSize:'13px', lineHeight:'1.8', color:'#e2e8f0', whiteSpace:'pre-wrap' }}>{aiSummary}</div>
              </div>
            )}
          </div>
        </>
      )}
      {!loading && reports.length === 0 && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '40px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <p style={{ color: '#999', fontSize: '16px' }}>Select a date and click Generate Report to view technician work data</p>
        </div>
      )}
    </div>
  );
};
export default TechWorkReport;
